import React from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { IStudentOpenResponse, IStudentOpenResponseAttempt, VerbaScholaApi } from '../api/VerbaScholaApi';
import VerbaScholaStudentOpenResponseFeedback from './VerbaScholaStudentOpenResponseFeedback';
import { StudentStepNavigation } from './StudentInstructionPresentation';
import { StudentRecordingPlayButton } from './StudentPlaybackButton';
import { formatRecordingTime, StudentRecordButton, StudentRecordingStatus, StudentRetryRecordButton, StudentSpeakingBanner, StudentStageSteps } from './StudentSpeakingControls';
import { STUDENT_BLUE, STUDENT_CORRECT_TINT, STUDENT_GREEN, STUDENT_INTER, STUDENT_NAVY, STUDENT_PANEL_RADIUS, STUDENT_POPPINS, STUDENT_RING, STUDENT_SLATE,
    STUDENT_SURFACE, studentBlueButtonSx, studentCardSx, studentEyebrowSx, studentSecondaryButtonSx } from '../style/verbaScholaStudentStyles';
import { chineseLang } from '../util/textLanguage';
import LessonText from './LessonText';

interface Props {
    stepId: number;
    content: IStudentOpenResponse;
    onComplete: () => void;
    onAdvance: () => void;
    onPrevious?: () => void;
    advanceLabel: string;
    preview?: boolean;
}

// Criteria groups carry the V2 markers: required points, ideas, and examples that are not requirements.
const criteriaGroups = [
    { role: 'required' as const, heading: 'Include in your answer', Icon: CheckCircleOutlineRoundedIcon },
    { role: 'suggested' as const, heading: 'Ideas to help you', Icon: LightbulbOutlinedIcon },
    { role: 'example' as const, heading: 'Example only', Icon: InfoOutlinedIcon },
];

// Recording stops here; the server refuses longer takes (verbaschola_open_response_speech.MAX_RECORDING_SECONDS).
const MAX_RECORDING_SECONDS = 300;
// Formats the server accepts, in the order the shared speaking recorder prefers them. Firefox would
// otherwise record Ogg by default; it is accepted too, as a last resort.
const RECORDING_TYPES = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav'];

function serverMessage(failure: unknown): string | null {
    const data = (failure as { response?: { data?: { message?: unknown; error?: unknown } | string } })?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
        if (typeof data.message === 'string' && data.message.trim()) return data.message;
        if (typeof data.error === 'string' && data.error.trim()) return data.error;
    }
    return null;
}

export default function VerbaScholaStudentOpenResponseCard({ stepId, content, onComplete, onAdvance, onPrevious, advanceLabel, preview = false }: Props) {
    const needsText = content.kind !== 'speak_open';
    const needsAudio = content.kind !== 'write_open';
    const live = content.feedback_mode === 'live';
    const [attempts, setAttempts] = React.useState<IStudentOpenResponseAttempt[]>([]);
    const [answer, setAnswer] = React.useState('');
    const [take, setTake] = React.useState<Blob | null>(null);
    const [takeUrl, setTakeUrl] = React.useState<string | null>(null);
    const [savedAudioUrl, setSavedAudioUrl] = React.useState<string | null>(null);
    const [playing, setPlaying] = React.useState(false);
    const [audioSeconds, setAudioSeconds] = React.useState<number | null>(null);
    const [recording, setRecording] = React.useState(false);
    const [recordingSeconds, setRecordingSeconds] = React.useState(0);
    const [starting, setStarting] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const [limitReached, setLimitReached] = React.useState(false);
    const recorderRef = React.useRef<MediaRecorder | null>(null);
    const streamRef = React.useRef<MediaStream | null>(null);
    const timerRef = React.useRef<number>();
    const recordingSecondsRef = React.useRef(0);
    const mountedRef = React.useRef(true);
    const requestPendingRef = React.useRef(false);
    // One key per request kind: a retried draft check reuses its key, but a key is never reused
    // for a final submission (the server would return the draft, not save a final).
    const requestKeyRef = React.useRef<{ key: string; isFinal: boolean } | null>(null);
    const initializedRef = React.useRef(false);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const focusFeedbackRef = React.useRef(false);
    const focusFinalRef = React.useRef(false);
    const onCompleteRef = React.useRef(onComplete);
    onCompleteRef.current = onComplete;

    const refresh = React.useCallback(async () => {
        if (preview) return;
        const response = await VerbaScholaApi.getStudentOpenResponses(stepId);
        if (!mountedRef.current) return;
        const rows = response.data.attempts;
        setAttempts(rows);
        if (!initializedRef.current) {
            initializedRef.current = true;
            setAnswer((rows.find(row => row.is_final) ?? [...rows].reverse().find(row => !row.is_final))?.written_text ?? '');
        }
    }, [stepId, preview]);

    React.useEffect(() => {
        mountedRef.current = true;
        initializedRef.current = false;
        if (preview) initializedRef.current = true;
        else void refresh().catch(() => { if (mountedRef.current) setError('Could not load your saved answers. Try reloading.'); });
        return () => {
            mountedRef.current = false;
            if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
            streamRef.current?.getTracks().forEach(track => track.stop());
            window.clearInterval(timerRef.current);
        };
    }, [refresh, preview]);

    React.useEffect(() => {
        if (!take) { setTakeUrl(null); return; }
        const url = URL.createObjectURL(take);
        setTakeUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [take]);

    React.useEffect(() => {
        audioRef.current?.pause();
        setPlaying(false);
    }, [takeUrl, savedAudioUrl]);

    const latestDraft = [...attempts].reverse().find(row => !row.is_final);
    const final = attempts.find(row => row.is_final);
    const playbackVersion = final ?? latestDraft;
    // Only a draft check is waited on here; a final's assessment is the teacher's to release.
    const pending = attempts.some(row => !row.is_final && (row.assessment_status === 'queued' || row.assessment_status === 'processing'));
    const finalId = final?.id;
    React.useEffect(() => {
        // A saved final completes the step, also when it was submitted in another tab or on an
        // earlier visit (a replayed lesson does not count earlier completion by itself).
        if (finalId != null && !preview) onCompleteRef.current();
    }, [finalId, preview]);
    React.useEffect(() => {
        if (!focusFeedbackRef.current) return;
        const latest = [...attempts].reverse().find(row => !row.is_final);
        if (latest && ['feedback_ready', 'failed'].includes(latest.assessment_status)) {
            focusFeedbackRef.current = false;
            // Do not pull focus (and close a phone keyboard) from a student who is typing or recording.
            const active = document.activeElement;
            if (!active || active === document.body || (active as HTMLElement).dataset?.openResponseAction === 'check') {
                document.getElementById(`open-response-feedback-${latest.id}`)?.focus();
            }
        }
    }, [attempts]);
    React.useEffect(() => {
        if (!focusFinalRef.current || !finalId) return;
        focusFinalRef.current = false;
        document.getElementById(`open-response-final-${stepId}`)?.focus();
    }, [finalId, stepId]);
    React.useEffect(() => {
        if (!pending) return;
        // Keep polling after a failed request, backing off, so one network error cannot leave
        // "Checking your answer" on screen until a reload.
        let stopped = false;
        let delay = 2000;
        let timer = 0;
        const poll = async () => {
            try { await refresh(); delay = 2000; } catch { delay = Math.min(delay * 2, 30000); }
            if (!stopped) timer = window.setTimeout(() => void poll(), delay);
        };
        timer = window.setTimeout(() => void poll(), delay);
        return () => { stopped = true; window.clearTimeout(timer); };
    }, [pending, refresh]);
    const awaitingTeacher = Boolean(final && !final.grade);
    React.useEffect(() => {
        if (!awaitingTeacher || preview || pending) return;
        const timer = window.setInterval(() => {
            if (document.visibilityState === 'visible') void refresh().catch(() => undefined);
        }, 20000);
        return () => window.clearInterval(timer);
    }, [awaitingTeacher, pending, preview, refresh]);

    React.useEffect(() => {
        if (!playbackVersion?.has_audio || take || !needsAudio) { setSavedAudioUrl(null); return; }
        let cancelled = false;
        let objectUrl: string | null = null;
        void VerbaScholaApi.getStudentOpenResponseAudio(stepId, playbackVersion.id).then(response => {
            if (!cancelled) { objectUrl = URL.createObjectURL(response.data); setSavedAudioUrl(objectUrl); }
        }).catch(() => undefined);
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [stepId, playbackVersion?.id, playbackVersion?.has_audio, take, needsAudio]);

    const resetSubmissionKey = () => { requestKeyRef.current = null; };
    const changeAnswer = (value: string) => {
        setAnswer(value);
        if (content.kind === 'type_speak_open') { setTake(null); setAudioSeconds(null); }
        resetSubmissionKey();
    };
    const startRecording = async () => {
        if (preview || busy || recording || requestPendingRef.current || final) return;
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            setError('Recording is unavailable in this browser.'); return;
        }
        requestPendingRef.current = true;
        setStarting(true);
        setError('');
        let stream: MediaStream | null = null;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (!mountedRef.current || final) { stream.getTracks().forEach(track => track.stop()); return; }
            const mimeType = RECORDING_TYPES.find(type => MediaRecorder.isTypeSupported?.(type));
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            const chunks: BlobPart[] = [];
            let failed = false;
            streamRef.current = stream;
            recorderRef.current = recorder;
            recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
            recorder.onerror = () => {
                failed = true;
                if (recorder.state === 'recording') recorder.stop();
                if (mountedRef.current) setError('Recording stopped unexpectedly. Please record again.');
            };
            recorder.onstop = () => {
                stream?.getTracks().forEach(track => track.stop());
                streamRef.current = null;
                window.clearInterval(timerRef.current);
                if (!mountedRef.current) return;
                setRecording(false);
                if (failed) return;
                if (!chunks.length) { setError('No audio was saved. Please try again.'); return; }
                setTake(new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }));
                setAudioSeconds(recordingSecondsRef.current);
                resetSubmissionKey();
            };
            recorder.start();
            setRecordingSeconds(0);
            setLimitReached(false);
            recordingSecondsRef.current = 0;
            timerRef.current = window.setInterval(() => {
                recordingSecondsRef.current += 1;
                setRecordingSeconds(recordingSecondsRef.current);
                if (recordingSecondsRef.current >= MAX_RECORDING_SECONDS && recorder.state === 'recording') {
                    setLimitReached(true);
                    recorder.stop();
                }
            }, 1000);
            setRecording(true);
        } catch {
            stream?.getTracks().forEach(track => track.stop());
            setError('Microphone access was unavailable. Check your browser permission.');
        } finally {
            requestPendingRef.current = false;
            if (mountedRef.current) setStarting(false);
        }
    };
    const stopRecording = () => { if (recorderRef.current?.state === 'recording') recorderRef.current.stop(); };
    // From the feedback, jump to where the student revises: the writing, or the recorder.
    const reviseAnswer = () => {
        const target = document.getElementById(needsText ? `open-response-answer-${stepId}` : `open-response-recording-${stepId}`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target?.focus({ preventScroll: true });
    };
    const matchesSaved = Boolean(latestDraft && !take && answer.trim() === (latestDraft.written_text ?? '') && (!needsAudio || latestDraft.has_audio));
    const hasResponse = (!needsText || answer.trim().length > 0) && (!needsAudio || Boolean(take || (matchesSaved && latestDraft?.has_audio)));
    const latestFailed = latestDraft?.assessment_status === 'failed';
    const canCheck = live && !pending && hasResponse && (!matchesSaved || latestFailed) && (!needsAudio || Boolean(take));
    const canFinish = hasResponse;
    const playbackUrl = recording || starting ? null : takeUrl ?? ((final || matchesSaved) ? savedAudioUrl : null);
    const togglePlayback = async () => {
        const player = audioRef.current;
        if (!player) return;
        if (!player.paused) { player.pause(); player.currentTime = 0; setPlaying(false); return; }
        try { await player.play(); setPlaying(true); }
        catch { setError('This recording could not be played. Try recording again.'); }
    };
    const submit = async (isFinal: boolean) => {
        if (preview || busy || starting || recording || requestPendingRef.current || !hasResponse || final) return;
        if (!isFinal && !canCheck) return;
        if (isFinal && !canFinish) return;
        setBusy(true);
        setError('');
        if (!isFinal) focusFeedbackRef.current = true;
        try {
            const key = requestKeyRef.current?.isFinal === isFinal ? requestKeyRef.current.key : crypto.randomUUID();
            requestKeyRef.current = { key, isFinal };
            const payload = { is_final: isFinal, idempotency_key: key,
                ...(isFinal && matchesSaved && latestDraft ? { source_attempt_id: latestDraft.id } : { written_text: needsText ? answer.trim() : undefined }) };
            const saved = (await VerbaScholaApi.submitStudentOpenResponse(stepId, payload, take ?? undefined)).data;
            requestKeyRef.current = null;
            if (saved.is_final !== isFinal) throw new Error('The saved version does not match this request');
            setTake(null);
            setAttempts(rows => [...rows.filter(row => row.id !== saved.id), saved].sort((a, b) => a.attempt_number - b.attempt_number));
            if (isFinal) { focusFinalRef.current = true; onComplete(); }
            void refresh().catch(() => undefined); // the saved version is already shown; polling catches up
        } catch (failure) {
            focusFeedbackRef.current = false;
            const status = (failure as { response?: { status?: number } })?.response?.status;
            if (status === 409) {
                setError(serverMessage(failure) ?? 'A saved answer is still being checked. Wait for feedback and try again.');
                void refresh().catch(() => undefined);
            } else if (status === 400) {
                // The server's reason is the useful part: a silent or over-long recording, for example.
                setError(serverMessage(failure) ?? 'Your answer could not be saved. Please check it and try again.');
            } else setError('Your answer could not be saved. Please retry; your response is still here.');
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    const hasTake = Boolean(take || (matchesSaved && latestDraft?.has_audio));
    const writeDone = Boolean(final) || answer.trim().length > 0;
    const sayDone = Boolean(final) || hasTake;
    const panelFocusSx = { '&:focus-visible': { outline: `3px solid ${STUDENT_BLUE}`, outlineOffset: 2 } };

    return <Stack spacing={2.5}>
        <Box sx={{ ...studentCardSx, overflow: 'hidden' }}>
            {needsAudio
                ? <StudentSpeakingBanner label={content.kind === 'type_speak_open' ? 'WRITE AND SPEAK' : 'SPEAK'} title={content.question} />
                : <Box sx={{ p: { xs: 2.5, md: 3.5 }, borderBottom: `1px solid ${STUDENT_RING}` }}>
                    <Typography sx={studentEyebrowSx}>WRITE YOUR ANSWER</Typography>
                    <Typography component="h3" sx={{ fontFamily: STUDENT_POPPINS, fontWeight: 600, fontSize: { xs: 21, md: 25 }, color: STUDENT_NAVY, mt: 1 }}>{content.title}</Typography>
                </Box>}
            <Stack spacing={2.5} sx={{ p: { xs: 2.5, md: 3.5 } }}>
                {preview && <Alert severity="info">Student view preview. Answers and feedback are available after this exercise is assigned.</Alert>}
                {content.guidance && <LessonText sx={{ fontFamily: STUDENT_INTER, color: STUDENT_SLATE }}>{content.guidance}</LessonText>}
                {content.kind === 'type_speak_open' && <StudentStageSteps steps={[
                    { label: '1 Write your answer', active: !final && !writeDone, complete: writeDone },
                    { label: '2 Say it aloud', active: !final && writeDone && !sayDone, complete: sayDone },
                ]} />}
                {!needsAudio && <Box><Typography sx={studentEyebrowSx}>YOUR QUESTION</Typography>
                    <Typography lang={chineseLang(content.question)} sx={{ mt: 1, fontFamily: STUDENT_POPPINS, fontWeight: 500, fontSize: { xs: 20, md: 23 }, color: STUDENT_NAVY }}>{content.question}</Typography></Box>}
                {content.criteria.length > 0 && <Stack spacing={2} sx={{ bgcolor: STUDENT_SURFACE, borderRadius: STUDENT_PANEL_RADIUS, p: 2 }}>
                    {criteriaGroups.map(({ role, heading, Icon }) => {
                        const rows = content.criteria.filter(item => item.role === role);
                        if (!rows.length) return null;
                        return <Box key={role}>
                            <Typography sx={{ fontFamily: STUDENT_POPPINS, fontWeight: 600, fontSize: 14, color: STUDENT_NAVY }}>{heading}</Typography>
                            <Stack component="ul" spacing={1} sx={{ m: 0, mt: 1.25, p: 0, listStyle: 'none' }}>
                                {rows.map(item => <Stack component="li" key={item.text} direction="row" spacing={1} alignItems="flex-start">
                                    <Icon aria-hidden sx={{ mt: '2px', fontSize: 17, color: role === 'required' ? STUDENT_BLUE : STUDENT_SLATE, flexShrink: 0 }} />
                                    <Typography sx={{ fontFamily: STUDENT_INTER, fontSize: 14, lineHeight: 1.5, color: STUDENT_NAVY, overflowWrap: 'anywhere' }}>{item.text}</Typography>
                                </Stack>)}
                            </Stack>
                        </Box>;
                    })}
                </Stack>}
                {needsText && <TextField id={`open-response-answer-${stepId}`} label="Your written answer" multiline minRows={5} value={answer} disabled={preview || Boolean(final) || busy || recording}
                    onChange={event => changeAnswer(event.target.value)} inputProps={{ lang: 'zh' }} />}
                {content.kind === 'type_speak_open' && (take || savedAudioUrl) && !final && <Typography sx={{ fontFamily: STUDENT_INTER, color: STUDENT_SLATE, fontSize: 12 }}>
                    Editing your answer clears the recording so your words and audio stay together.</Typography>}
                {needsAudio && <Box id={`open-response-recording-${stepId}`} tabIndex={-1} sx={{ border: `1px solid ${STUDENT_RING}`, borderRadius: STUDENT_PANEL_RADIUS, p: 2, ...panelFocusSx }}>
                    <Typography sx={{ fontFamily: STUDENT_POPPINS, fontWeight: 600, fontSize: 15, color: STUDENT_NAVY, mb: 1.5 }}>Your recording</Typography>
                    {recording ? <Stack spacing={1.5}>
                        <StudentRecordingStatus elapsed={recordingSeconds} maxSeconds={MAX_RECORDING_SECONDS} nested />
                        <StudentRecordButton recording onClick={stopRecording} />
                    </Stack> : playbackUrl ? <Stack spacing={1.25} alignItems="flex-start">
                        <Typography sx={{ fontFamily: STUDENT_INTER, fontWeight: 600, fontSize: 13, color: STUDENT_NAVY }}>
                            Take saved{audioSeconds != null ? ` · ${formatRecordingTime(audioSeconds)}` : ''}</Typography>
                        <audio ref={audioRef} src={playbackUrl} onEnded={() => setPlaying(false)}
                            onLoadedMetadata={event => {
                                const duration = event.currentTarget.duration;
                                if (Number.isFinite(duration)) setAudioSeconds(Math.round(duration));
                            }} style={{ display: 'none' }} />
                        <StudentRecordingPlayButton playing={playing} onClick={() => void togglePlayback()} />
                        {!final && <StudentRetryRecordButton label="Record again" starting={starting} disabled={preview || starting || busy || (needsText && !answer.trim())} onClick={() => void startRecording()} />}
                    </Stack> : !final && <StudentRecordButton recording={false} working={starting} workingLabel="Opening microphone…"
                        disabled={preview || starting || busy || (needsText && !answer.trim())} onClick={() => void startRecording()} />}
                    {content.speaking_hint && <LessonText sx={{ fontFamily: STUDENT_INTER, fontSize: 13, color: STUDENT_SLATE, mt: 1.5 }}>{content.speaking_hint}</LessonText>}
                    {limitReached && !recording && <Typography role="status" sx={{ fontFamily: STUDENT_INTER, color: STUDENT_SLATE, fontSize: 12, mt: 1 }}>
                        Recording stopped at the {MAX_RECORDING_SECONDS / 60}-minute limit. Listen back before you submit.</Typography>}
                    {!playbackUrl && !recording && <Typography sx={{ fontFamily: STUDENT_INTER, color: STUDENT_SLATE, fontSize: 12, mt: 1 }}>
                        {needsText && !answer.trim() ? 'Write your answer first.' : `Record, listen back, then submit when you are ready. Recordings can be up to ${MAX_RECORDING_SECONDS / 60} minutes.`}</Typography>}
                </Box>}
                {error && <Alert severity="error">{error}</Alert>}
                {live && latestDraft && [latestDraft].map(row => <Box key={row.id} id={`open-response-feedback-${row.id}`}
                    tabIndex={-1} sx={{ border: `1px solid ${STUDENT_RING}`, borderRadius: STUDENT_PANEL_RADIUS, p: 2, ...panelFocusSx }}>
                    <Typography component="h3" sx={{ fontFamily: STUDENT_POPPINS, fontWeight: 600, fontSize: 16, color: STUDENT_NAVY }}>Feedback on answer {row.attempt_number}</Typography>
                    {row.assessment_status === 'queued' || row.assessment_status === 'processing'
                        ? <Typography role="status" sx={{ fontFamily: STUDENT_INTER, mt: 1 }}>Checking your answer… You can leave and come back.</Typography>
                        : row.assessment_status === 'failed' ? <Typography sx={{ fontFamily: STUDENT_INTER, mt: 1 }}>{row.failure_reason === 'recording_too_short'
                            ? 'This recording was too short to check. Record your answer again, then ask for feedback.'
                            : row.failure_reason === 'sentence_too_long'
                            ? 'One part of your recording was longer than 28 seconds, the most that can be checked at once, so this recording could not be checked. Record again using shorter sentences, then ask for feedback.'
                            : row.failure_reason
                            ? 'We could not hear speech in this recording, so it could not be checked. Check your microphone, record again, then ask for feedback.'
                            : needsAudio
                            ? 'Feedback is unavailable for this answer right now. Your answer is saved: record again to ask for feedback, or finish and submit.'
                            : 'Feedback is unavailable for this answer right now. Your answer is saved: ask for feedback again, or finish and submit.'}</Typography>
                        : <VerbaScholaStudentOpenResponseFeedback stepId={stepId} attempt={row} speakOnly={content.kind === 'speak_open'}
                            onRevise={final ? undefined : reviseAnswer} />}
                    {row.assessment_status === 'feedback_ready' && !final && <Typography sx={{ fontFamily: STUDENT_INTER, mt: 1.5, color: STUDENT_SLATE, fontSize: 12 }}>
                        You can ask for more feedback or finish with your current answer.</Typography>}
                </Box>)}
                {final ? <Stack spacing={1.5} alignItems="flex-start">
                    {/* A saved final is a status, not an alert (V2): alert boxes are for failures to load or save. */}
                    <Stack direction="row" spacing={1} alignItems="flex-start" id={`open-response-final-${stepId}`} tabIndex={-1} role="status"
                        sx={{ alignSelf: 'stretch', p: 2, borderRadius: STUDENT_PANEL_RADIUS, bgcolor: STUDENT_CORRECT_TINT, border: `1px solid ${STUDENT_GREEN}40`, ...panelFocusSx }}>
                        <CheckCircleRoundedIcon aria-hidden sx={{ color: STUDENT_GREEN, fontSize: 22, flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: STUDENT_INTER, fontWeight: 600, color: STUDENT_NAVY }}>
                            {final.grade ? 'Your teacher has reviewed your final answer.' : 'Your final answer is saved. A teacher will release the grade after review.'}</Typography>
                    </Stack>
                    {final.grade && <Stack spacing={1.25} sx={{ alignSelf: 'stretch', p: 2, borderRadius: STUDENT_PANEL_RADIUS, border: `1px solid ${STUDENT_RING}`, fontFamily: STUDENT_INTER, color: STUDENT_NAVY }}>
                        <Typography sx={{ fontFamily: STUDENT_POPPINS, fontWeight: 600, fontSize: 18, color: STUDENT_NAVY }}>Grade: {final.grade.score}/100</Typography>
                        {final.grade.level && <Typography sx={{ fontFamily: STUDENT_INTER, fontWeight: 600 }}>Level: {final.grade.level}</Typography>}
                        {final.grade.comment && <LessonText sx={{ fontFamily: STUDENT_INTER, whiteSpace: 'pre-wrap' }}>{final.grade.comment}</LessonText>}
                        {final.grade.strengths && <Box><Typography sx={gradeHeadingSx}>What went well</Typography><LessonText sx={{ fontFamily: STUDENT_INTER, whiteSpace: 'pre-wrap' }}>{final.grade.strengths}</LessonText></Box>}
                        {final.grade.next_steps && <Box><Typography sx={gradeHeadingSx}>What to work on next</Typography><LessonText sx={{ fontFamily: STUDENT_INTER, whiteSpace: 'pre-wrap' }}>{final.grade.next_steps}</LessonText></Box>}
                        {Boolean(final.grade.reviewed_notes?.length) && <Box>
                            <Typography sx={gradeHeadingSx}>Language notes from your teacher</Typography>
                            {final.grade.reviewed_notes?.map((note, index) => <LessonText key={index} sx={{ fontFamily: STUDENT_INTER, whiteSpace: 'pre-wrap', mt: 0.5 }}>{note.text}</LessonText>)}
                        </Box>}
                    </Stack>}
                    <Button onClick={() => void refresh().catch(() => setError('Could not refresh teacher feedback. Try again.'))} sx={{ minHeight: 44 }}>
                        {final.grade ? 'Refresh teacher feedback' : 'Check for teacher feedback'}
                    </Button>
                </Stack> : <Stack spacing={1.25}>
                    {/* Submitting graded work is the blue primary (V2), distinct from the yellow next-step action. */}
                    <Button fullWidth variant="contained" disabled={preview || !canFinish || busy || starting || recording}
                        onClick={() => void submit(true)} sx={studentBlueButtonSx}>
                        {busy ? 'Saving…' : live ? 'Finish and submit' : 'Submit answer'}</Button>
                    {live && <Button fullWidth disabled={preview || !canCheck || busy || starting || recording} onClick={() => void submit(false)}
                        data-open-response-action="check" sx={studentSecondaryButtonSx}>
                        {latestFailed && matchesSaved ? 'Ask for AI feedback again' : attempts.length ? 'Get AI feedback on this revision' : 'Get AI feedback'}</Button>}
                    {live && <Typography sx={{ fontFamily: STUDENT_INTER, color: STUDENT_SLATE, fontSize: 12 }}>{needsAudio
                        ? 'You can submit now. Optional AI feedback checks your language and pronunciation. It takes longer than for writing, because each recorded sentence is transcribed and checked first. Your teacher reviews the required points.'
                        : 'You can submit now. Optional AI feedback checks language and may take about a minute. Your teacher reviews the required points.'}</Typography>}
                </Stack>}
            </Stack>
        </Box>
        {/* The shared lesson navigation, as in every other activity: Continue unlocks once the answer is submitted. */}
        <StudentStepNavigation onPrevious={onPrevious} onContinue={onAdvance} disabled={!final && !preview} label={advanceLabel} />
    </Stack>;
}

const gradeHeadingSx = { fontFamily: STUDENT_POPPINS, fontWeight: 600, fontSize: 14, color: STUDENT_NAVY } as const;
